
import React, { useState } from "react";

interface SimpleImageRendererProps {
  images: string[];
}

interface ImageState {
  [key: string]: 'loading' | 'loaded' | 'error';
}

const SimpleImageRenderer: React.FC<SimpleImageRendererProps> = ({ images }) => {
  const [imageStates, setImageStates] = useState<ImageState>({});
  
  console.log('🖼️ SIMPLE-RENDERER: Starting render with', images.length, 'images');
  console.log('🖼️ SIMPLE-RENDERER: Image details:', images.map((img, index) => ({
    index: index + 1,
    isDataUrl: img.startsWith('data:image/'),
    isUrl: img.startsWith('http'),
    length: img.length,
    prefix: img.substring(0, 100)
  })));
  
  if (images.length === 0) {
    console.log('🖼️ SIMPLE-RENDERER: No images to render');
    return null;
  }

  const handleImageLoad = (key: string) => {
    console.log(`✅ SIMPLE-RENDERER: Image ${key} loaded successfully`);
    setImageStates(prev => ({ ...prev, [key]: 'loaded' }));
  };

  const handleImageError = (key: string, src: string) => {
    console.error(`❌ SIMPLE-RENDERER: Image ${key} failed to load:`, {
      src: src.substring(0, 100),
      isDataUrl: src.startsWith('data:image/'),
      isUrl: src.startsWith('http')
    });
    setImageStates(prev => ({ ...prev, [key]: 'error' }));
  };

  return (
    <div className="mb-3 space-y-2">
      {images.map((imageData, index) => {
        const key = `image-${index}`;
        const state = imageStates[key] || 'loading';
        
        console.log(`🖼️ SIMPLE-RENDERER: Processing image ${index + 1}:`, {
          hasData: !!imageData,
          isDataUrl: imageData.startsWith('data:image/'),
          isUrl: imageData.startsWith('http'),
          length: imageData.length,
          prefix: imageData.substring(0, 50),
          state
        });
        
        // ENHANCED: Validate image data - accept both data URLs and HTTP URLs
        const isValidImage = imageData.startsWith('data:image/') || imageData.startsWith('http');
        
        if (!imageData || !isValidImage) {
          console.error(`❌ SIMPLE-RENDERER: Invalid image data for image ${index + 1}:`, {
            hasData: !!imageData,
            isDataUrl: imageData.startsWith('data:image/'),
            isUrl: imageData.startsWith('http'),
            actualPrefix: imageData?.substring(0, 50) || 'NO DATA'
          });
          return (
            <div key={key} className="text-red-500 text-sm p-2 border border-red-200 rounded">
              Failed to load image {index + 1}: Invalid format
              <div className="text-xs mt-1 text-muted-foreground">
                Expected data:image/ or http:// URL
              </div>
            </div>
          );
        }
        
        console.log(`✅ SIMPLE-RENDERER: Valid image ${index + 1}, proceeding to render`);
        
        return (
          <div key={key} className="inline-block">
            <img
              src={imageData}
              alt={`Uploaded image ${index + 1}`}
              className="max-w-full h-auto rounded-lg border border-gray-200"
              style={{ maxHeight: '300px', objectFit: 'contain' }}
              onError={() => handleImageError(key, imageData)}
              onLoad={() => handleImageLoad(key)}
            />
            {state === 'error' && (
              <div className="text-red-500 text-sm mt-1">
                <div>Failed to load image {index + 1}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {imageData.startsWith('http') ? (
                    <a 
                      href={imageData} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:opacity-80 underline"
                    >
                      Try opening image directly
                    </a>
                  ) : (
                    'Base64 image failed to load'
                  )}
                </div>
              </div>
            )}
            {state === 'loading' && (
              <div className="text-muted-foreground text-sm mt-1">
                Loading image...
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SimpleImageRenderer;
